/**
 * Quelle maille contient un point ? Fonctions pures, sans dépendance d'exécution :
 * le même code est vérifié contre PostGIS hors navigateur (tests/, outils/).
 *
 * Coordonnées en EPSG:4326, [longitude, latitude], comme en GeoJSON.
 */

export type Position = readonly number[]
export type Polygone = readonly (readonly Position[])[]

export interface GeometrieSurface {
  type: 'Polygon' | 'MultiPolygon'
  coordinates: Polygone | readonly Polygone[]
}

export interface EntiteSurface {
  id?: number | string
  geometry: GeometrieSurface | null
  properties?: Record<string, unknown> | null
}

const EPS = 1e-12

function surSegment(px: number, py: number, a: Position, b: Position): boolean {
  const vectoriel = (b[0] - a[0]) * (py - a[1]) - (b[1] - a[1]) * (px - a[0])
  if (Math.abs(vectoriel) > EPS) return false
  return px >= Math.min(a[0], b[0]) - EPS && px <= Math.max(a[0], b[0]) + EPS
      && py >= Math.min(a[1], b[1]) - EPS && py <= Math.max(a[1], b[1]) + EPS
}

/** Position d'un point par rapport à un anneau fermé : lancer de rayon, avec test de bord. */
export function situerDansAnneau(anneau: readonly Position[], px: number, py: number): 'dedans' | 'bord' | 'dehors' {
  let dedans = false
  const n = anneau.length
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = anneau[j], b = anneau[i]
    if (surSegment(px, py, a, b)) return 'bord'
    if ((b[1] > py) !== (a[1] > py) && px < ((a[0] - b[0]) * (py - b[1])) / (a[1] - b[1]) + b[0]) {
      dedans = !dedans
    }
  }
  return dedans ? 'dedans' : 'dehors'
}

/** Le polygone couvre-t-il le point, bord compris ? Un point strictement dans un trou est dehors. */
function couvrePolygone(polygone: Polygone, px: number, py: number): boolean {
  if (!polygone.length || situerDansAnneau(polygone[0], px, py) === 'dehors') return false
  for (let k = 1; k < polygone.length; k++) {
    if (situerDansAnneau(polygone[k], px, py) === 'dedans') return false
  }
  return true
}

/** Équivalent de ST_Covers(géométrie, point) pour un Polygon ou un MultiPolygon. */
export function couvre(g: GeometrieSurface | null, px: number, py: number): boolean {
  if (!g) return false
  if (g.type === 'Polygon') return couvrePolygone(g.coordinates as Polygone, px, py)
  return (g.coordinates as readonly Polygone[]).some((p) => couvrePolygone(p, px, py))
}

function idNumerique(e: EntiteSurface): number {
  const brut = e.id ?? e.properties?.id
  const n = Number(brut)
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY
}

/**
 * La maille qui couvre le point. Sur une limite partagée, deux mailles le couvrent :
 * on retient le plus petit id, pour que la réponse ne dépende pas de l'ordre de
 * chargement des tuiles (même règle que la vérification PostGIS : min(id)).
 */
export function mailleContenant<T extends EntiteSurface>(entites: Iterable<T>, px: number, py: number): T | null {
  let retenue: T | null = null
  let idRetenu = Number.POSITIVE_INFINITY
  for (const e of entites) {
    if (!couvre(e.geometry, px, py)) continue
    const id = idNumerique(e)
    if (retenue === null || id < idRetenu) { retenue = e; idRetenu = id }
  }
  return retenue
}

/** Clé de la tuile qui contient (lon, lat), pour des tuiles carrées de `taille` degrés. */
export function cleTuile(lon: number, lat: number, taille: number): string {
  return `${Math.floor(lon / taille)}_${Math.floor(lat / taille)}`
}

/**
 * Tuiles dont une maille peut couvrir le point : la sienne, et ses voisines s'il
 * est à moins de `marge` d'un bord (une maille est rangée selon son centroïde et
 * déborde au plus de sa demi-largeur).
 */
export function tuilesCandidates(lon: number, lat: number, existantes: ReadonlySet<string>, taille: number, marge: number): string[] {
  const cles = new Set<string>()
  for (const dx of [-marge, 0, marge]) {
    for (const dy of [-marge, 0, marge]) {
      const cle = cleTuile(lon + dx, lat + dy, taille)
      if (existantes.has(cle)) cles.add(cle)
    }
  }
  return [...cles]
}

/** Tuiles qui recouvrent une emprise [ouest, sud, est, nord], élargie de `marge`. */
export function tuilesEmprise(emprise: readonly number[], existantes: ReadonlySet<string>, taille: number, marge: number): string[] {
  const [x0, y0, x1, y1] = emprise
  const cles: string[] = []
  for (let x = Math.floor((x0 - marge) / taille); x <= Math.floor((x1 + marge) / taille); x++) {
    for (let y = Math.floor((y0 - marge) / taille); y <= Math.floor((y1 + marge) / taille); y++) {
      const cle = `${x}_${y}`
      if (existantes.has(cle)) cles.push(cle)
    }
  }
  return cles
}
